import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {ArchiveOutlined} from '@material-ui/icons';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import Message from '../../../shared/message/Message.js';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import {TIPO_PESSOA_INSCRITO} from '../../components/inscricao/arvoreDocumentos.js';
import useFetch from '../../../../hooks/useFetch.js';
import useCreateFile from '../../../../hooks/useCreateFile.js';
import {URL_AVALIACAO_DOCUMENTOS_ZIP, URL_AVALIACAO_DOCUMENTOS_ZIP_PESSOAS} from '../../../../constants/index.js';
import {salvarArquivoResposta} from '../../../../utils/download.js';

const PARAMS = {};
// Valor do select para o ZIP completo (sem o parâmetro `pessoa`).
const TODOS = 'TODOS';

const useStyles = makeStyles(() => ({
    root: {
        width: 500,
        maxWidth: '100%'
    },
    contentRoot: {overflowY: 'initial'}
}));

const plural = (quantidade, singular, varios) => `${quantidade} ${quantidade === 1 ? singular : varios}`;

const rotuloPessoa = pessoa => `${pessoa.tipo === TIPO_PESSOA_INSCRITO ? 'Inscrito' : 'Membro'} - ${pessoa.nome} (${pessoa.quantidade})`;

/*
 * ZIP dos documentos aprovados da avaliação: todos (como antes) ou só os de
 * uma pessoa — o inscrito ou um membro da composição familiar. As pessoas e as
 * quantidades vêm do back, com o mesmo filtro de aprovados do ZIP.
 */
export default function DialogBaixarZipAprovados(props) {
    const {open, onClose, idInscricao} = props;
    const classes = useStyles();
    const [buscar, carregando] = useFetch({returnInnerLoadingState: true});
    const [baixar, baixando] = useCreateFile({returnInnerLoadingState: true});
    const [pessoas, setPessoas] = useState(null);
    const [pessoa, setPessoa] = useState(TODOS);

    // Busca e download pelo LoadingModal global (sem spinner dentro do diálogo).
    useCarregamentoPagina(carregando || baixando);

    // A cada abertura volta para "Todos" e recarrega as pessoas (avaliações podem ter mudado).
    useEffect(() => {
        if (!open) {
            return;
        }

        setPessoas(null);
        setPessoa(TODOS);
        buscar(URL_AVALIACAO_DOCUMENTOS_ZIP_PESSOAS(idInscricao), PARAMS, lista => setPessoas(Array.isArray(lista) ? lista : []));
    }, [open, idInscricao]);

    const total = (pessoas || []).reduce((soma, item) => soma + (Number(item.quantidade) || 0), 0);
    const semAprovados = pessoas !== null && !pessoas.length;

    const handleBaixar = () => {
        const url = pessoa === TODOS
            ? URL_AVALIACAO_DOCUMENTOS_ZIP(idInscricao)
            : `${URL_AVALIACAO_DOCUMENTOS_ZIP(idInscricao)}?pessoa=${encodeURIComponent(pessoa)}`;

        baixar(url, {}, response => {
            salvarArquivoResposta(response, 'doc_aprovados.zip');
            onClose();
        });
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            classes={{paper: classes.root}}
            scroll='body'
            aria-labelledby='dialog-zip-aprovados-titulo'>
            <DialogTitle id='dialog-zip-aprovados-titulo'>Baixar Documentos Aprovados</DialogTitle>
            <DialogContent classes={{root: classes.contentRoot}}>
                {semAprovados ? (
                    <Message type='info'>Não há documentos aprovados para baixar.</Message>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <LabelValue label='De quem *'>
                                <FormControl variant='outlined' fullWidth disabled={pessoas === null}>
                                    <Select value={pessoa} onChange={evento => setPessoa(evento.target.value)}>
                                        <MenuItem value={TODOS}>{`Todos (${plural(total, 'documento', 'documentos')})`}</MenuItem>
                                        {(pessoas || []).map(item => (
                                            <MenuItem key={item.chave} value={item.chave}>{rotuloPessoa(item)}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </LabelValue>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose} disabled={baixando}>Cancelar</TextButton>
                <ContainedButton
                    startIcon={<ArchiveOutlined/>}
                    disabled={!pessoas || semAprovados || carregando || baixando}
                    onClick={handleBaixar}>
                    Baixar
                </ContainedButton>
            </DialogActions>
        </Dialog>
    );
}

DialogBaixarZipAprovados.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Id da inscrição avaliada. */
    idInscricao: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};
